import { useEffect, useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { Users as UsersIcon, X, Edit2, Shield, UserPlus } from 'lucide-react'

export default function Users() {
  const { profile, brands } = useAuth()
  const [users, setUsers] = useState([])
  const [modal, setModal] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState({ full_name: '', role: 'viewer', brand_ids: [] })
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteForm, setInviteForm] = useState({ email: '', full_name: '', role: 'viewer', brand_ids: [] })
  const [inviting, setInviting] = useState(false)
  const [inviteError, setInviteError] = useState('')

  useEffect(() => { load() }, [])

  async function load() {
    setLoading(true)
    const { data: profs } = await supabase.from('user_profiles').select('*').order('full_name')
    const { data: access } = await supabase.from('user_brand_access').select('user_id, brand_id')
    const usersWithAccess = (profs || []).map(u => ({
      ...u,
      brand_ids: (access || []).filter(a => a.user_id === u.id).map(a => a.brand_id)
    }))
    setUsers(usersWithAccess)
    setLoading(false)
  }

  function openEdit(u) {
    setEditing(u)
    setForm({ full_name: u.full_name || '', role: u.role, brand_ids: u.brand_ids })
    setError('')
    setModal(true)
  }

  async function save() {
    setSaving(true)
    setError('')

    const { error: profErr } = await supabase
      .from('user_profiles')
      .update({ full_name: form.full_name, role: form.role })
      .eq('id', editing.id)
    if (profErr) {
      setError(profErr.message || 'Failed to update user.')
      setSaving(false)
      return
    }

    const { error: delErr } = await supabase.from('user_brand_access').delete().eq('user_id', editing.id)
    if (delErr) {
      setError(delErr.message || 'Failed to update brand access.')
      setSaving(false)
      return
    }

    if (form.role !== 'super_admin' && form.brand_ids.length > 0) {
      const { error: insErr } = await supabase
        .from('user_brand_access')
        .insert(form.brand_ids.map(bid => ({ user_id: editing.id, brand_id: bid })))
      if (insErr) {
        setError(insErr.message || 'Failed to save brand access.')
        setSaving(false)
        return
      }
    }

    setSaving(false)
    setModal(false)
    load()
  }

  function toggleBrand(bid) {
    setForm(f => ({
      ...f,
      brand_ids: f.brand_ids.includes(bid) ? f.brand_ids.filter(b => b !== bid) : [...f.brand_ids, bid]
    }))
  }

  function openInvite() {
    setInviteForm({ email: '', full_name: '', role: 'viewer', brand_ids: [] })
    setInviteError('')
    setInviteOpen(true)
  }

  function toggleInviteBrand(bid) {
    setInviteForm(f => ({
      ...f,
      brand_ids: f.brand_ids.includes(bid) ? f.brand_ids.filter(b => b !== bid) : [...f.brand_ids, bid]
    }))
  }

  async function sendInvite() {
    setInviteError('')
    if (!inviteForm.email.trim()) { setInviteError('Email is required.'); return }
    if (inviteForm.role !== 'super_admin' && inviteForm.brand_ids.length === 0) {
      setInviteError('Select at least one brand for this role.'); return
    }
    setInviting(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('Your session expired — please sign in again.')
      const res = await fetch('/api/invite-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          email: inviteForm.email.trim(),
          full_name: inviteForm.full_name.trim(),
          role: inviteForm.role,
          brand_ids: inviteForm.brand_ids
        })
      })
      const data = await res.json()
      if (!res.ok || data.error) throw new Error(data.error || 'Invite failed')
      setInviteOpen(false)
      load()
    } catch (e) {
      setInviteError(e.message)
    }
    setInviting(false)
  }

  const brandName = (bid) => brands.find(b => b.id === bid)?.name || bid

  if (profile?.role !== 'super_admin') {
    return <div className="empty-state"><Shield size={40} /><p>Super admin access required</p></div>
  }

  return (
    <div className="page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
        <div>
          <h1>Users</h1>
          <p style={{ color: 'var(--text-3)', fontSize: '13px' }}>Invite teammates and assign their role and brand access.</p>
        </div>
        <button className="btn btn-primary" onClick={openInvite} style={{ flexShrink: 0 }}>
          <UserPlus size={15} /> Invite User
        </button>
      </div>

      <div className="card">
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
        ) : users.length === 0 ? (
          <div className="empty-state"><UsersIcon size={40} /><p>No users yet</p></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Name</th><th>Email</th><th>Role</th><th>Brand Access</th><th></th></tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td style={{ fontWeight: 500 }}>{u.full_name || '—'}</td>
                    <td style={{ color: 'var(--text-2)', fontFamily: 'var(--mono)', fontSize: '12px' }}>{u.email}</td>
                    <td>
                      <span className={`badge ${u.role === 'super_admin' || u.role === 'brand_super_admin' ? 'badge-accent' : u.role === 'brand_admin' ? 'badge-info' : 'badge-neutral'}`}>
                        {u.role.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td>
                      {u.role === 'super_admin' ? (
                        <span style={{ color: 'var(--text-3)', fontSize: '12px' }}>All brands</span>
                      ) : u.brand_ids.length === 0 ? (
                        <span style={{ color: 'var(--danger)', fontSize: '12px' }}>No access</span>
                      ) : (
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          {u.brand_ids.map(bid => <span key={bid} className="badge badge-neutral">{brandName(bid)}</span>)}
                        </div>
                      )}
                    </td>
                    <td><button className="btn btn-ghost btn-sm" onClick={() => openEdit(u)}><Edit2 size={13} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card" style={{ background: 'var(--accent-bg)', borderColor: 'rgba(108,142,247,0.2)' }}>
        <div style={{ fontSize: '13px', color: 'var(--text-2)', lineHeight: 1.6 }}>
          <strong style={{ color: 'var(--accent)' }}>Inviting a user:</strong> Click <strong>Invite User</strong>, enter their email, and pick their role and brand access. They'll get an email with a link to set their password — their role and access are applied right away, so they're ready as soon as they sign in.
        </div>
      </div>

      {modal && editing && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <h2>Edit User</h2>
              <button className="btn btn-ghost btn-sm" onClick={() => setModal(false)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: '12px', color: 'var(--text-3)', marginBottom: '4px' }}>Email</div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: '13px', marginBottom: '16px' }}>{editing.email}</div>
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input className="input" value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Role</label>
                <select className="select" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                  <option value="viewer">Viewer</option>
                  <option value="brand_admin">Brand Admin</option>
                  <option value="brand_super_admin">Brand Super Admin</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>
              {form.role !== 'super_admin' && (
                <div className="form-group">
                  <label className="form-label">Brand Access</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
                    {brands.map(b => (
                      <label key={b.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
                        <input type="checkbox" checked={form.brand_ids.includes(b.id)} onChange={() => toggleBrand(b.id)} />
                        {b.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {error && (
              <div style={{ margin: '0 20px 12px', padding: '10px 12px', background: 'rgba(229,72,77,0.1)', border: '1px solid var(--danger)', borderRadius: 'var(--radius)', color: 'var(--danger)', fontSize: '12px', lineHeight: 1.5 }}>
                {error}
              </div>
            )}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Update User'}</button>
            </div>
          </div>
        </div>
      )}

      {inviteOpen && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setInviteOpen(false)}>
          <div className="modal">
            <div className="modal-header">
              <h2>Invite User</h2>
              <button className="btn btn-ghost btn-sm" onClick={() => setInviteOpen(false)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  className="input"
                  type="email"
                  value={inviteForm.email}
                  onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="name@company.com"
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input className="input" value={inviteForm.full_name} onChange={e => setInviteForm(f => ({ ...f, full_name: e.target.value }))} placeholder="Optional" />
              </div>
              <div className="form-group">
                <label className="form-label">Role</label>
                <select className="select" value={inviteForm.role} onChange={e => setInviteForm(f => ({ ...f, role: e.target.value }))}>
                  <option value="viewer">Viewer</option>
                  <option value="brand_admin">Brand Admin</option>
                  <option value="brand_super_admin">Brand Super Admin</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>
              {inviteForm.role !== 'super_admin' && (
                <div className="form-group">
                  <label className="form-label">Brand Access</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
                    {brands.map(b => (
                      <label key={b.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
                        <input type="checkbox" checked={inviteForm.brand_ids.includes(b.id)} onChange={() => toggleInviteBrand(b.id)} />
                        {b.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {inviteError && (
              <div style={{ margin: '0 20px 12px', padding: '10px 12px', background: 'rgba(229,72,77,0.1)', border: '1px solid var(--danger)', borderRadius: 'var(--radius)', color: 'var(--danger)', fontSize: '12px', lineHeight: 1.5 }}>
                {inviteError}
              </div>
            )}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setInviteOpen(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={sendInvite} disabled={inviting}>{inviting ? 'Sending...' : 'Send Invite'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
